<!--
  - SPDX-FileCopyrightText: 2022 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<NcListItem
		:name="title"
		:active="isSelected"
		:to="{ name: 'note', params: { noteId: note.id.toString() } }"
		:draggable="isDraggable"
		oneLine
		@update:menuOpen="onMenuChange"
		@click="onNoteSelected(note.id)"
		@dragstart="onDragStart"
	>
		<template v-if="showCategoryTitle && note.category" #subname>
			{{ categoryTitle }}
		</template>
		<template #icon>
			<AlertOctagonOutlineIcon v-if="note.error"
				:size="20"
				fillColor="#E9322D"
			/>
			<StarIcon v-else-if="note.favorite"
				:size="20"
				fillColor="#FC0"
			/>
		</template>
		<template v-if="isShared" #indicator>
			<ShareVariantOutlineIcon :size="16" fillColor="#0082c9" />
		</template>
		<template #actions>
			<NcActionButton :icon="actionFavoriteIcon" @click="onToggleFavorite">
				{{ actionFavoriteText }}
			</NcActionButton>

			<NcActionButton @click="openSidebar('sharing')">
				<template #icon>
					<ShareVariantOutlineIcon :size="20" />
				</template>
				{{ t('notes', 'Share') }}
			</NcActionButton>

			<NcActionButton v-if="hasVersionsTab()" @click="openSidebar('files_versions')">
				<template #icon>
					<BackupRestoreIcon :size="20" />
				</template>
				{{ t('notes', 'Versions') }}
			</NcActionButton>

			<NcActionButton @click="openSidebar('notes-info')">
				<template #icon>
					<InformationOutlineIcon :size="20" />
				</template>
				{{ t('notes', 'Details') }}
			</NcActionButton>

			<NcActionButton v-if="!showCategorySelect" @click="showCategorySelect = true">
				<template #icon>
					<FolderOutlineIcon :size="20" />
				</template>
				{{ categoryTitle }}
			</NcActionButton>
			<NcActionInput
				v-else
				:modelValue="note.category"
				type="multiselect"
				:label="t('notes', 'Change category')"
				:labelOutside="false"
				:multiple="false"
				:options="categories"
				:disabled="loading.category"
				:taggable="true"
				@update:modelValue="onCategoryChange"
				@searchChange="onCategoryChange"
			>
				<template #icon>
					<FolderOutlineIcon :size="20" />
				</template>
				{{ t('notes', 'Change category') }}
			</NcActionInput>

			<NcActionButton v-if="!renaming" @click="startRenaming">
				<template #icon>
					<PencilOutlineIcon :size="20" />
				</template>
				{{ t('notes', 'Rename') }}
			</NcActionButton>
			<NcActionInput v-else
				v-model.trim="newTitle"
				:disabled="!renaming"
				:placeholder="t('notes', 'Rename note')"
				:showTrailingButton="true"
				@submit="onRename"
			>
				<template #icon>
					<PencilOutlineIcon :size="20" />
				</template>
			</NcActionInput>

			<NcActionSeparator />

			<NcActionButton v-if="!note.readonly"
				:icon="actionDeleteIcon"
				:closeAfterClick="true"
				@click="onDeleteNote"
			>
				{{ t('notes', 'Delete note') }}
			</NcActionButton>
		</template>
	</NcListItem>
</template>

<script>
import { showError } from '@nextcloud/dialogs'
import { emit, subscribe, unsubscribe } from '@nextcloud/event-bus'
import { getSidebarTabs } from '@nextcloud/files'
import NcActionButton from '@nextcloud/vue/components/NcActionButton'
import NcActionInput from '@nextcloud/vue/components/NcActionInput'
import NcActionSeparator from '@nextcloud/vue/components/NcActionSeparator'
import NcListItem from '@nextcloud/vue/components/NcListItem'
import AlertOctagonOutlineIcon from 'vue-material-design-icons/AlertOctagonOutline.vue'
import BackupRestoreIcon from 'vue-material-design-icons/BackupRestore.vue'
import FolderOutlineIcon from 'vue-material-design-icons/FolderOutline.vue'
import InformationOutlineIcon from 'vue-material-design-icons/InformationOutline.vue'
import PencilOutlineIcon from 'vue-material-design-icons/PencilOutline.vue'
import ShareVariantOutlineIcon from 'vue-material-design-icons/ShareVariantOutline.vue'
import StarIcon from 'vue-material-design-icons/Star.vue'
import logger from '../Logger.js'
import { deleteNote, fetchNote, setCategory, setFavorite, setTitle } from '../NotesService.js'
import store from '../store.js'
import { categoryLabel, routeIsNewNote } from '../Util.js'

export default {
	name: 'NoteItem',

	components: {
		AlertOctagonOutlineIcon,
		BackupRestoreIcon,
		FolderOutlineIcon,
		InformationOutlineIcon,
		NcActionButton,
		NcListItem,
		StarIcon,
		NcActionSeparator,
		NcActionInput,
		PencilOutlineIcon,
		ShareVariantOutlineIcon,
	},

	props: {
		note: {
			type: Object,
			required: true,
		},

		showCategoryTitle: {
			type: Boolean,
			default: false,
		},
	},

	emits: [
		'categorySelected',
		'noteDeleted',
		'noteSelected',
		'startRenaming',
	],

	data() {
		return {
			loading: {
				note: false,
				category: false,
				favorite: false,
			},

			newTitle: '',
			renaming: false,
			showCategorySelect: false,
			isShareCreated: false,
		}
	},

	computed: {
		isDraggable() {
			return !this.note.readonly
		},

		isSelected() {
			return store.notes.getSelectedNote() === this.note.id
		},

		isShared() {
			return this.note.isShared || this.isShareCreated
		},

		title() {
			return this.note.title + (this.note.unsaved ? ' *' : '')
		},

		categoryTitle() {
			return categoryLabel(this.note.category)
		},

		actionFavoriteText() {
			return this.note.favorite ? this.t('notes', 'Remove from favorites') : this.t('notes', 'Add to favorites')
		},

		actionFavoriteIcon() {
			let icon = this.note.favorite ? 'icon-star-dark' : 'icon-starred'
			if (this.loading.favorite) {
				icon += ' loading'
			}
			return icon
		},

		actionCategoryText() {
			return categoryLabel(this.note.category)
		},

		actionDeleteIcon() {
			return 'icon-delete' + (this.loading.delete ? ' loading' : '')
		},

		categories() {
			return [
				{
					id: '',
					label: categoryLabel(''),
				},
				...store.notes.getCategories(0, false).map((category) => ({
					id: category,
					label: categoryLabel(category),
				})),
			]
		},
	},

	mounted() {
		subscribe('files_sharing:share:created', this.onShareCreated)
	},

	unmounted() {
		unsubscribe('files_sharing:share:created', this.onShareCreated)
	},

	methods: {
		onDragStart(event) {
			if (!this.isDraggable) {
				event.preventDefault()
				return
			}

			const noteId = this.note.id.toString()
			event.dataTransfer.effectAllowed = 'move'
			try {
				event.dataTransfer.setData('application/x-nextcloud-notes-note-id', noteId)
			} catch {
				// Some browsers only allow specific mime types.
			}
			event.dataTransfer.setData('text/plain', noteId)
		},

		onMenuChange(state) {
			this.actionsOpen = state
			this.showCategorySelect = false
		},

		onNoteSelected(noteId) {
			this.$emit('noteSelected', noteId)
		},

		onToggleFavorite() {
			this.loading.favorite = true
			setFavorite(this.note.id, !this.note.favorite)
				.catch(() => {
				})
				.then(() => {
					this.loading.favorite = false
					this.actionsOpen = false
				})
		},

		onCategorySelected() {
			this.actionsOpen = false
			this.$emit('categorySelected', this.note.category)
		},

		startRenaming() {
			this.renaming = true
			this.newTitle = this.note.title
			this.$emit('startRenaming', this.note.id)
		},

		async onCategoryChange(result) {
			this.showCategorySelect = false
			const category = result?.id ?? result?.label ?? null
			if (category !== null && this.note.category !== category) {
				this.loading.category = true
				await setCategory(this.note.id, category)
				this.loading.category = false
			}
		},

		async onRename() {
			const newTitle = this.newTitle.toString()
			if (!newTitle) {
				return
			}
			this.loading.note = true
			setTitle(this.note.id, newTitle)
				.then(() => {
					this.newTitle = ''
				})
				.catch((e) => {
					logger.error('Failed to rename note', { error: e })
					showError(this.t('notes', 'Error while renaming note.'))
				})
				.finally(() => {
					this.loading.note = false
				})

			if (routeIsNewNote(this.$route)) {
				this.$router.replace({
					name: 'note',
					params: { noteId: this.note.id.toString() },
				})
			}
			this.renaming = false
		},

		async onDeleteNote() {
			this.loading.delete = true
			try {
				const note = await fetchNote(this.note.id)
				if (note.errorType) {
					throw new Error('Note has errors')
				}
				await deleteNote(this.note.id, () => {
					this.$emit('noteDeleted', note)
					this.loading.delete = false
					this.actionsOpen = false
				})
			} catch (e) {
				logger.error('Error during preparing note for deletion', { error: e })
				showError(this.t('notes', 'Error during preparing note for deletion.'))
				this.loading.delete = false
				this.actionsOpen = false
			}
		},

		hasVersionsTab() {
			return getSidebarTabs().some((tab) => tab?.id === 'files_versions')
		},

		openSidebar(tab) {
			this.actionsOpen = false
			emit('notes:sidebar:open', { noteId: this.note.id, tab })
		},

		async onShareCreated(event) {
			const { share } = event

			if (share.fileSource === this.note.id) {
				this.isShareCreated = true
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.material-design-icon {
	width: var(--default-clickable-area);
	.list-item__wrapper--active & {
		color: var(--color-primary-element-text) !important;
	}
}

:deep(.list-item) {
	padding: 0;
}

:deep(.list-item-content__subname) {
	text-align: end;
}

:deep(.list-item__anchor) {
	box-sizing: border-box;
	height: calc(var(--list-item-height) + 2 * var(--default-grid-baseline));
	padding: var(--list-item-padding);
}
</style>
